/**
 * KAN-837: редактор "Когда мне можно звонить": дни недели кнопками Пн..Вс
 * и время с/до. Управляемый (черновик и логика в lib/workHours и хуках),
 * один на приложение менеджера и карточку менеджера в кабинете.
 * Крупные зоны нажатия (от 44px) для телефона.
 */
import { useId } from 'react';
import { cn } from '@/lib/utils';
import { WEEKDAYS, timezoneHint, toggleWorkDay, type WorkHoursDraft } from '@/lib/workHours';

interface Props {
  value: WorkHoursDraft;
  onChange: (value: WorkHoursDraft) => void;
  /** Заголовок группы; по умолчанию без заголовка (его даёт родитель). */
  label?: string;
  timezone?: string | null;
  error?: string | null;
  disabled?: boolean;
}

export function WorkHoursEditor({ value, onChange, label, timezone, error, disabled = false }: Props): JSX.Element {
  const id = useId();
  const hint = timezoneHint(timezone);
  const describedBy = [hint ? `${id}-tz` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;

  return (
    <fieldset className="space-y-3" aria-describedby={describedBy} data-testid="work-hours-editor" disabled={disabled}>
      {label && <legend className="mb-1 text-sm font-medium text-gray-900">{label}</legend>}
      <div className="grid grid-cols-7 gap-1.5" role="group" aria-label="Дни недели">
        {WEEKDAYS.map(({ day, short, full }) => {
          const on = value.days.includes(day);
          return (
            <button
              key={day}
              type="button"
              aria-pressed={on}
              aria-label={full}
              title={full}
              onClick={() => onChange(toggleWorkDay(value, day))}
              className={cn(
                'flex h-11 min-w-0 items-center justify-center rounded-lg border text-sm font-medium transition-colors disabled:opacity-50',
                on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 active:bg-gray-100'
              )}
            >
              {short}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-sm text-gray-700">
          С
          <input
            type="time"
            value={value.start}
            onChange={(e) => onChange({ ...value, start: e.target.value })}
            aria-invalid={!!error}
            className="h-12 w-full rounded-xl border border-gray-300 bg-white px-3 text-base outline-none focus:border-blue-500"
            data-testid="work-hours-start"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-gray-700">
          До
          <input
            type="time"
            value={value.end}
            onChange={(e) => onChange({ ...value, end: e.target.value })}
            aria-invalid={!!error}
            className="h-12 w-full rounded-xl border border-gray-300 bg-white px-3 text-base outline-none focus:border-blue-500"
            data-testid="work-hours-end"
          />
        </label>
      </div>
      {hint && (
        <p id={`${id}-tz`} className="text-xs text-gray-500" data-testid="work-hours-timezone">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-sm text-red-600" data-testid="work-hours-error">
          {error}
        </p>
      )}
    </fieldset>
  );
}
